import React, { useState, useRef, useCallback } from 'react';
import '../../Css/Botones/botones.css';
import '../../Css/catalogobase/catalogobase.css';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import Navbar from '../../components/empnavbar/empnavbar';
import Sidebar from '../../components/empsidebar/empsidebar';
import { API_URLS, request } from '../../Services/servicioURL';

// Interfaces
interface EmpleadoLoggeado {
    cveemp: string;
    nomemp: string;
    email?: string;
}

interface FormState {
    passwordActual: string;
    passwordNueva: string;
    confirmarPassword: string;
}

interface FormErrors {
    passwordActual?: string;
    passwordNueva?: string;
    confirmarPassword?: string;
}

// Función para obtener el empleado loggeado
const obtenerEmpleadoLoggeado = (): EmpleadoLoggeado | null => {
    try {
        const authToken = localStorage.getItem('authToken2');
        if (!authToken || authToken !== 'logueado') {
            return null;
        }

        const cveemp = localStorage.getItem('cveEmpleado');
        const nomemp = localStorage.getItem('nombreEmpleado');
        
        if (!cveemp || !nomemp) {
            return null;
        }

        return { cveemp, nomemp };
    } catch (error) {
        console.error('Error al obtener empleado loggeado:', error);
        return null;
    }
};

// Validaciones
const validarPassword = (password: string): string | null => {
    if (!password) {
        return 'La contraseña es obligatoria';
    }
    if (password.length < 6) {
        return 'La contraseña debe tener al menos 6 caracteres';
    }
    if (password.length > 50) {
        return 'La contraseña no puede tener más de 50 caracteres';
    }
    return null;
};

const validarFormulario = (formState: FormState): FormErrors => {
    const errors: FormErrors = {};
    
    if (!formState.passwordActual) {
        errors.passwordActual = 'La contraseña actual es obligatoria';
    }
    
    const errorNuevaPassword = validarPassword(formState.passwordNueva);
    if (errorNuevaPassword) {
        errors.passwordNueva = errorNuevaPassword;
    }
    
    if (!formState.confirmarPassword) {
        errors.confirmarPassword = 'Debe confirmar la nueva contraseña';
    } else if (formState.passwordNueva !== formState.confirmarPassword) {
        errors.confirmarPassword = 'Las contraseñas no coinciden';
    }
    
    if (formState.passwordActual && formState.passwordNueva && 
        formState.passwordActual === formState.passwordNueva) {
        errors.passwordNueva = 'La nueva contraseña debe ser diferente a la actual';
    }
    
    return errors;
};

// Función mejorada para manejar errores HTTP
const manejarErrorHTTP = (error: any): { mensaje: string; esPasswordIncorrecta: boolean } => {
    console.error('🔍 FRONTEND - Analizando error:', error);
    
    let mensaje = 'Error al cambiar la contraseña';
    let esPasswordIncorrecta = false;
    
    // Si el error tiene información de respuesta HTTP
    if (error?.response) {
        const status = error.response.status;
        const responseText = error.response.statusText || '';
        
        console.log('🔍 FRONTEND - Status HTTP:', status);
        console.log('🔍 FRONTEND - Response text:', responseText);
        
        switch (status) {
            case 400:
                mensaje = 'La contraseña actual es incorrecta';
                esPasswordIncorrecta = true;
                break;
            case 401:
                mensaje = 'La contraseña actual es incorrecta';
                esPasswordIncorrecta = true;
                break;
            case 404:
                mensaje = 'Servicio no disponible. Contacte al administrador.';
                break;
            case 500:
                mensaje = 'Error interno del servidor. Contacte al administrador.';
                break;
            default:
                mensaje = `Error del servidor (${status}). Intente nuevamente.`;
        }
    }
    // Si el error tiene un mensaje específico
    else if (error?.message) {
        const errorMsg = error.message.toLowerCase();
        
        console.log('🔍 FRONTEND - Error message:', errorMsg);
        
        // Detectar errores de contraseña incorrecta por palabras clave
        if (errorMsg.includes('password') && (errorMsg.includes('incorrect') || errorMsg.includes('wrong') || errorMsg.includes('invalid'))) {
            mensaje = 'La contraseña actual es incorrecta';
            esPasswordIncorrecta = true;
        }
        else if (errorMsg.includes('contraseña') && (errorMsg.includes('incorrecta') || errorMsg.includes('inválida') || errorMsg.includes('actual'))) {
            mensaje = 'La contraseña actual es incorrecta';
            esPasswordIncorrecta = true;
        }
        else if (errorMsg.includes('unauthorized') || errorMsg.includes('401')) {
            mensaje = 'La contraseña actual es incorrecta';
            esPasswordIncorrecta = true;
        }
        else if (errorMsg.includes('bad request') || errorMsg.includes('400')) {
            mensaje = 'La contraseña actual es incorrecta';
            esPasswordIncorrecta = true;
        }
        else if (errorMsg.includes('404') || errorMsg.includes('not found')) {
            mensaje = 'Servicio no disponible. Contacte al administrador.';
        }
        else if (errorMsg.includes('500') || errorMsg.includes('internal server error')) {
            mensaje = 'Error interno del servidor. Contacte al administrador.';
        }
        else if (errorMsg.includes('network') || errorMsg.includes('fetch') || errorMsg.includes('conexión')) {
            mensaje = 'Error de conexión. Verifique que el servidor esté funcionando.';
        }
        else if (errorMsg.includes('error en la petición')) {
            // Este es el mensaje genérico de la función request()
            // Probablemente sea un error 400/401 que indica contraseña incorrecta
            mensaje = 'La contraseña actual es incorrecta';
            esPasswordIncorrecta = true;
        }
        else {
            mensaje = error.message;
        }
    }
    // Error de red o conexión
    else if (error?.name === 'TypeError' || error?.name === 'NetworkError') {
        mensaje = 'Error de conexión. Verifique que el servidor esté funcionando.';
    }
    // Error desconocido
    else {
        mensaje = 'Error inesperado. Intente nuevamente.';
    }
    
    return { mensaje, esPasswordIncorrecta };
};

// Componente principal
const CambiarPassword: React.FC = () => {
    const [formState, setFormState] = useState<FormState>({
        passwordActual: '',
        passwordNueva: '',
        confirmarPassword: ''
    });
    const [errors, setErrors] = useState<FormErrors>({});
    const [loading, setLoading] = useState<boolean>(false);
    const [empleadoLoggeado] = useState<EmpleadoLoggeado | null>(obtenerEmpleadoLoggeado());
    
    const toast = useRef<Toast>(null);

    const updateFormState = useCallback((updates: Partial<FormState>) => {
        setFormState(prev => ({ ...prev, ...updates }));
        setErrors(prev => {
            const newErrors = { ...prev };
            Object.keys(updates).forEach(key => {
                delete newErrors[key as keyof FormErrors];
            });
            return newErrors;
        });
    }, []);

    const handleLimpiar = useCallback(() => {
        setFormState({
            passwordActual: '',
            passwordNueva: '',
            confirmarPassword: ''
        });
        setErrors({});
    }, []);

    const handleCambiarPassword = useCallback(async () => {
        console.log('🔍 FRONTEND - Iniciando cambio de contraseña...');
        
        if (!empleadoLoggeado) {
            console.log('🔍 FRONTEND - Error: No hay empleado loggeado');
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudo identificar al empleado loggeado',
                life: 3000
            });
            return;
        }

        console.log('🔍 FRONTEND - Empleado loggeado:', empleadoLoggeado);

        const formErrors = validarFormulario(formState);
        if (Object.keys(formErrors).length > 0) {
            console.log('🔍 FRONTEND - Errores de validación:', formErrors);
            setErrors(formErrors);
            toast.current?.show({
                severity: 'warn',
                summary: 'Datos incompletos',
                detail: 'Por favor corrija los errores en el formulario',
                life: 3000
            });
            return;
        }

        setLoading(true);
        
        // Usar el endpoint específico de cambiar contraseña
        const payload = {
            cveemp: empleadoLoggeado.cveemp,
            passwordActual: formState.passwordActual,
            passwordNueva: formState.passwordNueva
        };

        console.log('🔍 FRONTEND - URL:', API_URLS.empleados.cambiarPassword);
        console.log('🔍 FRONTEND - Payload:', {
            cveemp: payload.cveemp,
            passwordActual: payload.passwordActual ? '***' : 'undefined',
            passwordNueva: payload.passwordNueva ? '***' : 'undefined'
        });

        try {
            console.log('🔍 FRONTEND - Enviando petición...');
            
            const resultado = await request(
                API_URLS.empleados.cambiarPassword, 
                'PATCH', 
                payload
            );

            console.log('🔍 FRONTEND - Resultado exitoso:', resultado);

            toast.current?.show({
                severity: 'success',
                summary: 'Éxito',
                detail: 'Contraseña actualizada correctamente',
                life: 3000
            });

            handleLimpiar();
        } catch (error: any) {
            console.error('🔍 FRONTEND - Error completo:', error);
            
            const { mensaje, esPasswordIncorrecta } = manejarErrorHTTP(error);
            
            console.log('🔍 FRONTEND - Mensaje de error procesado:', mensaje);
            console.log('🔍 FRONTEND - Es password incorrecta:', esPasswordIncorrecta);
            
            // Si es error de contraseña incorrecta, marcar el campo como inválido
            if (esPasswordIncorrecta) {
                setErrors({ passwordActual: 'Contraseña incorrecta' });
            }
            
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: mensaje,
                life: 5000
            });
        } finally {
            setLoading(false);
            console.log('🔍 FRONTEND - Proceso terminado');
        }
    }, [empleadoLoggeado, formState, handleLimpiar]);

    // Función de test para verificar conectividad
    const testConectividad = useCallback(async () => {
        console.log('🧪 FRONTEND - Probando conectividad...');
        try {
            const response = await fetch(API_URLS.empleados.obtener);
            console.log('🧪 FRONTEND - Test conectividad - Status:', response.status);
            
            if (response.ok) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Conectividad OK',
                    detail: 'La conexión al servidor funciona correctamente',
                    life: 3000
                });
            } else {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Problema de conectividad',
                    detail: `Status: ${response.status}`,
                    life: 3000
                });
            }
        } catch (error) {
            console.error('🧪 FRONTEND - Error en test de conectividad:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Sin conexión',
                detail: 'No se puede conectar al servidor',
                life: 3000
            });
        }
    }, []);

    if (!empleadoLoggeado) {
        return (
            <>
                
                
                <Toast ref={toast} />
                
                <div className="content-wrapper" style={{
                    backgroundImage: 'url(/dist/img/FondoLogin.png)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundAttachment: 'fixed',
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <Card style={{ maxWidth: '400px', textAlign: 'center' }}>
                        <div style={{ padding: '20px' }}>
                            <i className="pi pi-exclamation-triangle" style={{ fontSize: '3rem', color: '#f39c12', marginBottom: '20px' }}></i>
                            <h3>Sesión no válida</h3>
                            <p>No se pudo identificar al empleado loggeado. Por favor, inicie sesión nuevamente.</p>
                            <Button 
                                label="Ir al Login" 
                                icon="pi pi-sign-in" 
                                className="p-button-primary"
                                onClick={() => window.location.href = '/login-empleados'}
                                style={{ marginTop: '15px' }}
                            />
                        </div>
                    </Card>
                </div>
            </>
        );
    }

    return (
        <>
            <Sidebar />
            <Navbar />
            <Toast ref={toast} />
            
            <div className="content-wrapper" style={{
                backgroundImage: 'url(/dist/img/FondoLogin.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'fixed',
                minHeight: '100vh',
                position: 'relative'
            }}>
                
                <div className="FormABC" style={{ maxWidth: '600px', margin: '0 auto' }}>
                    <div className="FormABC-header">
                        <h5>🔐 Cambiar Contraseña</h5>
                    </div>
                    
                    {/* Información del empleado */}
                    <div style={{ 
                        padding: '15px', 
                        backgroundColor: '#e3f2fd', 
                        borderRadius: '8px', 
                        marginBottom: '20px',
                        border: '1px solid #2196f3'
                    }}>
                        <h6 style={{ 
                            margin: '0 0 10px 0', 
                            color: '#1565c0', 
                            fontWeight: 'bold'
                        }}>
                            👤 Empleado
                        </h6>
                        <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <div>
                                <strong>Código:</strong> {empleadoLoggeado.cveemp}
                            </div>
                            <div>
                                <strong>Nombre:</strong> {empleadoLoggeado.nomemp}
                            </div>
                            
                        </div>
                    </div>

                    <Divider />

                    {/* Formulario de cambio de contraseña */}
                    <div className="FormABC-content">
                        <form onSubmit={(e) => e.preventDefault()}>
                            
                            <div className="form-row">
                                <div className="form-group full-width">
                                    <label>
                                        Contraseña Actual <span style={{ color: 'red' }}>*</span>
                                    </label>
                                    <Password
                                        value={formState.passwordActual}
                                        onChange={(e) => updateFormState({ passwordActual: e.target.value })}
                                        placeholder="Ingrese su contraseña actual"
                                        feedback={false}
                                        toggleMask
                                        className={errors.passwordActual ? 'p-invalid' : ''}
                                        style={{ 
                                            width: '100%',
                                            display: 'block'
                                        }}
                                        inputStyle={{ 
                                            width: '100%',
                                            paddingRight: '3rem',
                                            fontFamily: 'monospace'
                                        }}
                                        panelStyle={{ display: 'none' }}
                                    />
                                    {errors.passwordActual && (
                                        <small className="p-error" style={{ display: 'block', marginTop: '5px' }}>
                                            {errors.passwordActual}
                                        </small>
                                    )}
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group full-width">
                                    <label>
                                        Nueva Contraseña <span style={{ color: 'red' }}>*</span>
                                    </label>
                                    <Password
                                        value={formState.passwordNueva}
                                        onChange={(e) => updateFormState({ passwordNueva: e.target.value })}
                                        placeholder="Ingrese la nueva contraseña"
                                        toggleMask
                                        className={errors.passwordNueva ? 'p-invalid' : ''}
                                        style={{ 
                                            width: '100%',
                                            display: 'block'
                                        }}
                                        inputStyle={{ 
                                            width: '100%',
                                            paddingRight: '3rem',
                                            fontFamily: 'monospace'
                                        }}
                                        promptLabel="Ingrese una contraseña"
                                        weakLabel="Débil"
                                        mediumLabel="Media"
                                        strongLabel="Fuerte"
                                        mediumRegex="^(((?=.*[a-z])(?=.*[A-Z]))|((?=.*[a-z])(?=.*[0-9]))|((?=.*[A-Z])(?=.*[0-9])))(?=.{6,})"
                                        strongRegex="^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})"
                                    />
                                    {errors.passwordNueva && (
                                        <small className="p-error" style={{ display: 'block', marginTop: '5px' }}>
                                            {errors.passwordNueva}
                                        </small>
                                    )}
                                    <small style={{ display: 'block', marginTop: '5px', color: '#6c757d' }}>
                                        La contraseña debe tener entre 6 y 50 caracteres
                                    </small>
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group full-width">
                                    <label>
                                        Confirmar Nueva Contraseña <span style={{ color: 'red' }}>*</span>
                                    </label>
                                    <Password
                                        value={formState.confirmarPassword}
                                        onChange={(e) => updateFormState({ confirmarPassword: e.target.value })}
                                        placeholder="Confirme la nueva contraseña"
                                        feedback={false}
                                        toggleMask
                                        className={errors.confirmarPassword ? 'p-invalid' : ''}
                                        style={{ 
                                            width: '100%',
                                            display: 'block'
                                        }}
                                        inputStyle={{ 
                                            width: '100%',
                                            paddingRight: '3rem',
                                            fontFamily: 'monospace'
                                        }}
                                        panelStyle={{ display: 'none' }}
                                    />
                                    {errors.confirmarPassword && (
                                        <small className="p-error" style={{ display: 'block', marginTop: '5px' }}>
                                            {errors.confirmarPassword}
                                        </small>
                                    )}
                                </div>
                            </div>

                            {/* Botones */}
                            <div style={{ 
                                display: 'flex', 
                                gap: '10px', 
                                justifyContent: 'center', 
                                marginTop: '30px',
                                padding: '20px 0'
                            }}>
                                <Button
                                    label="Cambiar Contraseña"
                                    icon="pi pi-key"
                                    className="p-button-success p-button-raised"
                                    onClick={handleCambiarPassword}
                                    loading={loading}
                                    disabled={loading}
                                />
                                <Button
                                    label="Limpiar"
                                    icon="pi pi-refresh"
                                    className="p-button-secondary p-button-raised"
                                    onClick={handleLimpiar}
                                    disabled={loading}
                                />
                            </div>

                           

                            {/* Información de seguridad */}
                            <div style={{ 
                                marginTop: '20px', 
                                padding: '15px', 
                                backgroundColor: '#fff3cd', 
                                borderRadius: '8px',
                                border: '1px solid #ffc107'
                            }}>
                                <h6 style={{ 
                                    margin: '0 0 10px 0', 
                                    color: '#856404', 
                                    fontWeight: 'bold'
                                }}>
                                    🛡️ Recomendaciones de Seguridad
                                </h6>
                                <ul style={{ margin: '0', paddingLeft: '20px', color: '#856404' }}>
                                    <li>Use una contraseña única que no haya utilizado antes</li>
                                    <li>Combine letras mayúsculas, minúsculas, números y símbolos</li>
                                    <li>No comparta su contraseña con otras personas</li>
                                    <li>Cambie su contraseña regularmente</li>
                                </ul>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CambiarPassword;